import React, { Fragment, useEffect, useState } from "react";
import { Grid } from "@mui/material";
import { InfluencerProfileService } from "../../../core/services/influencer-profile.service";
import { Link } from "react-router-dom";
import { LineChartDropDown } from "../../../components/charts/line-chart";
import { AdvancedColumnChart } from "../../../components/charts/advanced-column-chart";
import InfluencerProfileLeft from "./influencer-profile-left";
import FollowerSection from "./followers/follower-sec";
import FollowerSecInline from "./followers/follower-sec-inline";
import PostFrequency from "./followers/post-frequency";
import Audience from "./audience/audience";
import Engagement from "./engagement/engagement";
import InstagramIcon1 from "../../../components/icons/instagram-icon1";
import InsightMediaDetailCard from "./post-card";
import InsightMediaReelsCard from "./reels-card";
import InsightMediaImageCard from "./image-card";
import InsightMediaLatestPostCard from "./latest-post";
import ContentLoading from "../../../components/loader/loading";
import useBodyClass from "../../../hooks/useBodyClass";
import * as gtUtil from "../../../core/utility";
import dayjs from "dayjs";
import moment from "moment";

const inflProfileService: InfluencerProfileService = new InfluencerProfileService();

// Capitals lookup for Union Territories — kept in sync with profile-insight.tsx
const utCapitals: any = {
  "Andaman and Nicobar Islands": "Port Blair",
  "Chandigarh": "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu": "Daman",
  "Delhi": "New Delhi",
  "Jammu and Kashmir": "Srinagar (Summer), Jammu (Winter)",
  "Ladakh": "Leh",
  "Lakshadweep": "Kavaratti",
  "Puducherry": "Puducherry",
};

// Quarterly grouping helper — copied from profile-insight.tsx so the shared
// (public) view renders the Advanced Insights charts identically.
const buildQuarterlyData = (
  data: any[],
  valueKey: string,
  isAverage: boolean = false
) => {
  const quarterlyMap: any = {};

  data.forEach((item) => {
    if (!item?.updated_at) return;

    const date = moment(item.updated_at);
    const year = date.year();
    const quarter = Math.floor(date.month() / 3) + 1;
    const key = `${year}-Q${quarter}`;

    if (!quarterlyMap[key]) {
      quarterlyMap[key] = {
        metricSum: 0,
        count: 0,
        postsSum: 0,
        lastDate: date,
      };
    }

    const metricValue = Number(item[valueKey]) || 0;
    const postsValue = Number(item.total_posts_availaible) || 0;

    quarterlyMap[key].metricSum += metricValue;
    quarterlyMap[key].postsSum += postsValue;
    quarterlyMap[key].count += 1;

    if (date.isAfter(quarterlyMap[key].lastDate)) {
      quarterlyMap[key].lastDate = date;
    }
  });

  return Object.entries(quarterlyMap).map(([key, q]: any) => {
    const year = q.lastDate.year();
    const quarter = Math.floor(q.lastDate.month() / 3);

    const startMonth = moment().quarter(quarter + 1).startOf("quarter");
    const endMonth = moment().quarter(quarter + 1).endOf("quarter");

    const label = `${startMonth.format("MMM")}–${endMonth.format("MMM")} ${String(year).slice(-2)}`;

    return {
      name: label,
      y: isAverage ? Number((q.metricSum / q.count).toFixed(0)) : q.metricSum,
      totalPosts: q.postsSum,
      isAverage,
    };
  });
};

export const ShareProfileInsight: React.FC = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const accessKey = urlParams.get("accessKey");

  const [loading, setIsLoading] = useState(false);
  const [dataInitialized, setDataInitialized] = useState(false);
  const [invalidLink, setInvalidLink] = useState(false);

  const [audienceHealthAccess] = useState(false);
  const [isAudienceHealthState, setIsAudienceHealthState] = useState("False");
  const [selectInflu, setSelectInflu] = useState<any>([]);
  const [influencerData, setInfluencerData] = useState<any>([]);
  const [genderSplitPieChartData, setGenderSplitPieChartData] = useState<any[]>([]);
  const [columnChartData, setColumnChartData] = useState<any[]>([]);
  const [genderArrayLabel, setGenderArrayLabel] = useState();
  const [postErComparisonData, setPostErComparisonData] = useState<any[]>([]);
  const [value, setValue] = useState("1");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [location, setLocation] = useState("");
  const [categories, setCategories] = useState([]);
  const [language, setLanguages] = useState([]);
  const [subscription_status, setSubscription_status] = useState<any>({
    status: null,
    plan_start_date: null,
    plan_end_date: null,
  });

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
  };

  useBodyClass("page-profile-insight");

  // Maps the profile-insight response into local state — kept identical to the
  // authenticated page (profile-insight.tsx :: getProfileInsight) so the public
  // view stays in sync with any data-shape changes.
  const mapProfileResponse = (response: any) => {
    setInfluencerData(response?.data?.rows);
    setSelectInflu(response?.data?.rows?.profile_detail);
    setIsAudienceHealthState(response?.data?.rows?.profile_detail?.[0]?.audience_health);

    const data = response?.data?.rows?.location_category_language?.[0];
    if (data) {
      let locationString = "";
      if (data?.state && utCapitals[data.state]) {
        if (data.state === "Delhi") {
          locationString = `New Delhi, ${data.state}, ${data?.country ? data.country : ""}`;
        } else {
          locationString = ` ${utCapitals[data.state]}, ${data?.city ? data.city : ""}, ${data?.country ? data.country : ""}`;
        }
      } else {
        locationString = `${data?.city ? data.city : ""}, ${data?.state ? data.state : ""}, ${data?.country ? data.country : ""}`;
      }
      setLocation(locationString);

      const categoryList: any = [
        data?.category1,
        data?.category2,
        data?.category3,
        data?.category4,
      ].filter((category) => category !== null);
      setCategories(categoryList);

      const languageList: any = [
        data?.lang1,
        data?.lang2,
        data?.lang3,
        data?.lang4,
      ].filter((lang) => lang !== null);
      setLanguages(languageList);
    }

    const genderSplitData = response?.data?.rows?.detail;
    if (Array.isArray(genderSplitData) && genderSplitData.length > 0) {
      const maleArray = genderSplitData[0]?.total_male_count || 0;
      const femaleArray = genderSplitData[0]?.total_female_count || 0;
      setGenderSplitPieChartData([
        { name: "Male", y: maleArray, color: "#497FD6" },
        { name: "Female", y: femaleArray, color: "#DD5A5D" },
      ]);
    }

    setPhone(response?.data?.rows?.email_phone?.[0]?.primary_phone);
    setEmail(response?.data?.rows?.email_phone?.[0]?.primary_email);
    setSubscription_status(response?.data?.rows?.subscription_status);

    const ageGenderData = response?.data?.rows?.age_gender;
    if (Array.isArray(ageGenderData)) {
      let mGenderArray = ageGenderData
        .filter((item) => item.age_gender_range.startsWith("M_") || item.age_gender_range.startsWith("M."))
        .map((item) => item.value);

      let fGenderArray = ageGenderData
        .filter((item) => item.age_gender_range.startsWith("F_") || item.age_gender_range.startsWith("F."))
        .map((item) => item.value);

      let genderArrayLabelSet = new Set(ageGenderData.map((item) => item.age_gender_range.replace(/[FMU_, F.M.U.]/g, "")));
      let genderLabel: any = [...genderArrayLabelSet];
      genderLabel.sort();
      setGenderArrayLabel(genderLabel);

      setColumnChartData([
        { name: "Male", data: mGenderArray, color: "#497FD6" },
        { name: "Female", data: fGenderArray, color: "#DD5A5D" },
      ]);
    }

    const erComparisonData = response?.data?.rows?.detail;
    if (Array.isArray(erComparisonData) && erComparisonData.length > 0) {
      let imageArray = ((erComparisonData[0]?.image_posts_est_reach_comparison + erComparisonData[0]?.carousel_posts_est_reach_comparison) / 2) || 0;
      let reelArray = erComparisonData[0]?.reels_posts_est_reach_comparison || 0;
      setPostErComparisonData([
        {
          name: "Images",
          type: "column",
          groupPadding: 0.8,
          data: [{ name: "image", value: imageArray }],
          color: "#0A7447",
        },
        {
          name: "Reels",
          type: "column",
          groupPadding: 0.8,
          data: [{ name: "reel", value: reelArray }],
          color: "#34E098",
        },
      ]);
    }
  };

  useEffect(() => {
    const fetchSharedProfile = async () => {
      if (!accessKey) {
        setInvalidLink(true);
        setDataInitialized(true);
        return;
      }
      setIsLoading(true);
      setDataInitialized(false);
      try {
        const verifyRes = await inflProfileService.verifySharedProfileInsight({ accessKey });
        if (verifyRes?.data?.status === 1 && verifyRes?.data?.rows?.length > 0) {
          const { sm_id, influencer_id } = verifyRes.data.rows[0];
          const response = await inflProfileService.getSharedProfileInsight({
            sm_id,
            influencer_id,
            accessKey,
          });
          if (response.status === gtUtil.Constants.SUCCESS) {
            mapProfileResponse(response);
          } else {
            setInvalidLink(true);
          }
        } else {
          console.error("Invalid or expired token:", verifyRes?.info);
          setInvalidLink(true);
        }
      } catch (error) {
        console.error("Error verifying shared profile:", error);
        setInvalidLink(true);
      } finally {
        setIsLoading(false);
        setDataInitialized(true);
      }
    };

    fetchSharedProfile();
  }, [accessKey]);

  // -------- Advanced Insights chart data (mirrors profile-insight.tsx) --------
  const followers = influencerData?.advanced_graph?.map((i: any) => i.followers);
  const mediaCount = influencerData?.advanced_graph?.map((i: any) => i.media_count);

  const quarterlyAvgLikesData = buildQuarterlyData(influencerData?.advanced_graph || [], "public_avg_likes");
  const quarterlyAvgCommentsData = buildQuarterlyData(influencerData?.advanced_graph || [], "public_avg_comments");
  const quarterlyTotalLikesData = buildQuarterlyData(influencerData?.advanced_graph || [], "total_like_public_profile");
  const quarterlyTotalCommentsData = buildQuarterlyData(influencerData?.advanced_graph || [], "total_comments_public_profile");

  const dates = influencerData?.advanced_graph?.map((i: any) =>
    new Date(i.updated_at).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "2-digit",
    })
  );
  let datesLabel: any = dates;

  const followersDataSource = [
    {
      name: "Followers",
      data: followers?.map((y: any, index: number) => ({ name: datesLabel[index], y })) || [],
    },
  ];

  const MediaCountDataSource = [
    {
      name: "Total Posts",
      data: mediaCount?.map((y: any, index: number) => ({
        name: moment(dates[index]).format("DD MMM YY"),
        y,
        rangeName: moment(dates[index]).format("DD MMM YY"),
      })),
    },
  ];

  const avgCommentsDataSource = [{ name: "Avg. Comments", data: quarterlyAvgCommentsData }];
  const avgLikesDataSource = [{ name: "Avg. Likes", data: quarterlyAvgLikesData }];
  const totalLikesDataSource = [{ name: "Likes", data: quarterlyTotalLikesData }];
  const totalCommentsDataSource = [{ name: "Comments", data: quarterlyTotalCommentsData }];

  if (!dataInitialized) {
    return <ContentLoading width="125" height="125" />;
  }

  if (invalidLink) {
    return (
      <div style={{ textAlign: "center", padding: "4rem 1rem" }}>
        This share link is invalid or has expired.
      </div>
    );
  }

  return (
    <Fragment>
      <div className="container">
        <div className="influencer-profile">
          <div className="influencer-profile__left">
            <InfluencerProfileLeft
              influencerData={influencerData}
              email={email}
              phone={phone}
              location={location}
              language={language}
              category={categories}
              loading={loading}
              subscription_status={subscription_status}
            />
          </div>
          <div className="influencer-profile__right">
            <div className="d-flex flex-wrap w-100 align-items-center mb-3">
              <div className="d-none d-lg-flex align-items-center">
                {influencerData?.profile_detail?.[0]?.username && (
                  <InstagramIcon1 width="20px" height="20px" className="mb-1" />
                )}
                <Link
                  target="_blank"
                  className="btn btn-text-link"
                  to={`https://www.instagram.com/${influencerData?.profile_detail?.[0]?.username}`}
                >
                  <span>{influencerData?.profile_detail?.[0]?.username}</span>
                </Link>
              </div>
              <span className="profile-updated">
                {`Last updated on ${dayjs(influencerData.updated_at).format("MMM D, YYYY")}`}
              </span>
            </div>

            <div className="followers-info d-none d-md-flex flex-column">
              <FollowerSection loading={loading} influencerData={influencerData} />
              <FollowerSecInline loading={loading} influencerData={influencerData} />
              <PostFrequency loading={loading} influencerData={influencerData} />
            </div>

            {/* POSTS DATA MOBILE */}
            <div className="d-md-none w-100">
              <FollowerSection influencerData={influencerData} loading={loading} />
              <strong className="influencer-profile__common__title audience pb-2 d-block">Posts Data</strong>
              <FollowerSecInline influencerData={influencerData} loading={loading} />
            </div>

            {(loading ||
              influencerData?.age_gender?.length > 0 ||
              genderSplitPieChartData[0]?.y > 0 ||
              genderSplitPieChartData[1]?.y > 0 ||
              influencerData?.city_country?.length > 0 ||
              influencerData?.city_indian?.length > 0 ||
              influencerData?.city_international?.length > 0) && (
              <Audience
                isAudienceHealthAccess={audienceHealthAccess}
                isAudienceHealthState={isAudienceHealthState}
                handleAudienceHealthClick={() => {}}
                genderSplitPieChartData={genderSplitPieChartData}
                influencerData={influencerData}
                columnChartData={columnChartData}
                genderArrayLabel={genderArrayLabel}
                loading={loading}
                handleChange={handleChange}
                value={value}
              />
            )}

            {/* ENGAGEMENT */}
            {(loading ||
              influencerData?.profile_data?.[0]?.reels_total_reach ||
              influencerData?.profile_data?.[0]?.image_total_reach ||
              influencerData?.profile_data?.[0]?.carousel_avg_impressions ||
              postErComparisonData[0]?.data[0]?.value > 0 ||
              postErComparisonData[1]?.data[0]?.value > 0) && (
              <Engagement
                influencerData={influencerData}
                loading={loading}
                postErComparisonData={postErComparisonData}
              />
            )}

            <strong className="influencer-profile__common__title audience position-relative">Advanced Insights</strong>
            <Grid container spacing={{ xs: 0, sm: 3 }} className="mb-3">
              {followersDataSource?.[0]?.data?.length > 0 && (
                <Grid item xs={12} sm={12} md={6} className="influencer-profile__common__inner">
                  <div className="influencer-profile__common__box">
                    <div className="inner-heading">Followers</div>
                    <LineChartDropDown
                      dataSource={followersDataSource}
                      categories={datesLabel}
                      exporting={false}
                      legend={false}
                      chartType="line"
                      height="240px"
                      lineWidth={2}
                    />
                  </div>
                </Grid>
              )}
              {MediaCountDataSource?.[0]?.data?.length > 0 && (
                <Grid item xs={12} sm={12} md={6} className="influencer-profile__common__inner">
                  <div className="influencer-profile__common__box">
                    <div className="inner-heading">Total Posts</div>
                    <LineChartDropDown
                      dataSource={MediaCountDataSource}
                      categories={datesLabel}
                      exporting={false}
                      legend={false}
                      chartType="line"
                      height="240px"
                      lineWidth={2}
                    />
                  </div>
                </Grid>
              )}
              {avgLikesDataSource?.[0]?.data?.length > 0 && (
                <Grid item xs={12} sm={12} md={6} className="influencer-profile__common__inner">
                  <div className="influencer-profile__common__box">
                    <div className="inner-heading">Average Likes</div>
                    <AdvancedColumnChart id="average-like" dataSource={avgLikesDataSource} barWidth={10} height="300px" legend={false} />
                  </div>
                </Grid>
              )}
              {avgCommentsDataSource?.[0]?.data?.length > 0 && (
                <Grid item xs={12} sm={12} md={6} className="influencer-profile__common__inner">
                  <div className="influencer-profile__common__box">
                    <div className="inner-heading">Average Comments</div>
                    <AdvancedColumnChart id="average-comment" dataSource={avgCommentsDataSource} barWidth={10} height="300px" legend={false} />
                  </div>
                </Grid>
              )}
              {totalLikesDataSource?.[0]?.data?.length > 0 && (
                <Grid item xs={12} sm={12} md={6} className="influencer-profile__common__inner">
                  <div className="influencer-profile__common__box">
                    <div className="inner-heading">Likes</div>
                    <AdvancedColumnChart id="total-like" dataSource={totalLikesDataSource} barWidth={10} height="300px" legend={false} />
                  </div>
                </Grid>
              )}
              {totalCommentsDataSource?.[0]?.data?.length > 0 && (
                <Grid item xs={12} sm={12} md={6} className="influencer-profile__common__inner">
                  <div className="influencer-profile__common__box">
                    <div className="inner-heading">Comments</div>
                    <AdvancedColumnChart id="total-comments" dataSource={totalCommentsDataSource} barWidth={10} height="300px" legend={false} />
                  </div>
                </Grid>
              )}
            </Grid>

            {(influencerData?.top_stories?.length !== 0 ||
              influencerData?.most_viewed_reels?.length !== 0 ||
              influencerData?.most_liked_images?.length !== 0) && (
              <strong className="influencer-profile__common__title pb-1">Posts</strong>
            )}

            {(loading || influencerData?.top_stories?.length > 0) && (
              <section className="sec-media-posts">
                <div className="container custom-container">
                  <strong className="sec-media-posts__title">Stories</strong>
                  <InsightMediaDetailCard influencerData={influencerData} loading={loading} />
                </div>
              </section>
            )}

            {(loading || influencerData?.latest_posts?.length > 0) && (
              <section className="sec-media-posts">
                <div className="container custom-container">
                  <strong className="sec-media-posts__title">Latest Posts</strong>
                  <InsightMediaLatestPostCard influencerData={influencerData} loading={loading} />
                </div>
              </section>
            )}

            {(loading || influencerData?.most_viewed_reels?.length > 0) && (
              <section className="sec-media-posts">
                <div className="container custom-container">
                  <strong className="sec-media-posts__title">Most Liked Reels</strong>
                  <InsightMediaReelsCard influencerData={influencerData} loading={loading} />
                </div>
              </section>
            )}

            {(loading || influencerData?.most_liked_images?.length > 0 || influencerData?.most_liked_carousel?.length > 0) && (
              <section className="sec-media-posts">
                <div className="container custom-container">
                  <strong className="sec-media-posts__title">Most Viewed Images</strong>
                  <InsightMediaImageCard influencerData={influencerData} loading={loading} />
                </div>
              </section>
            )}
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default ShareProfileInsight;
